import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../App';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import axios from 'axios';
import { toast } from 'sonner';

const MaterialesClases = () => {
  const { user, API } = useContext(AuthContext);
  const navigate = useNavigate();
  const [clases, setClases] = useState([]);
  const [materialesPorClase, setMaterialesPorClase] = useState({});
  const [loading, setLoading] = useState(true);
  const [filtroEstado, setFiltroEstado] = useState('activas');

  const isAdmin = user?.rol === 'admin';
  const isProfesor = user?.rol === 'profesor';

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const clasesRes = await axios.get(`${API}/clases`);
      let clasesData = clasesRes.data || [];

      // Filtrar clases según rol
      if (user.rol === 'estudiante' || user.rol === 'pastor' || user.rol === 'graduado') {
        clasesData = clasesData.filter(c =>
          c.estudiantes_inscritos?.includes(user.id)
        );
      } else if (user.rol === 'profesor') {
        clasesData = clasesData.filter(c => c.profesor_id === user.id);
      }

      setClases(clasesData);

      // Cargar materiales para cada clase
      const materialesMap = {};
      await Promise.all(
        clasesData.map(async (clase) => {
          try {
            const res = await axios.get(`${API}/clases/${clase.id}/materiales`);
            materialesMap[clase.id] = res.data || [];
          } catch {
            materialesMap[clase.id] = [];
          }
        })
      );
      setMaterialesPorClase(materialesMap);
    } catch (error) {
      console.error('Error cargando datos:', error);
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const getBannerStyle = (bannerUrl) => {
    if (bannerUrl) {
      const imageUrl = bannerUrl.startsWith('http') ? bannerUrl : `${API}${bannerUrl}`;
      return {
        backgroundImage: `linear-gradient(rgba(0,0,0,0.3), rgba(0,0,0,0.5)), url(${imageUrl})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      };
    }
    return {
      background: 'linear-gradient(135deg, #059669 0%, #065f46 100%)'
    };
  };

  const getFileIcon = (filename) => {
    if (!filename) return 'fas fa-file';
    const ext = filename.split('.').pop()?.toLowerCase();
    if (ext === 'pdf') return 'fas fa-file-pdf text-red-500';
    if (['ppt', 'pptx'].includes(ext)) return 'fas fa-file-powerpoint text-orange-500';
    if (['doc', 'docx'].includes(ext)) return 'fas fa-file-word text-blue-500';
    if (['xls', 'xlsx'].includes(ext)) return 'fas fa-file-excel text-green-500';
    return 'fas fa-file text-gray-500';
  };

  const clasesFiltradas = clases.filter(c => {
    if (filtroEstado === 'activas') return c.activa;
    if (filtroEstado === 'inactivas') return !c.activa;
    return true;
  });

  const totalMateriales = Object.values(materialesPorClase).reduce((acc, m) => acc + m.length, 0);
  const clasesConMateriales = Object.values(materialesPorClase).filter(m => m.length > 0).length;

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50 p-4 sm:p-6 flex items-center justify-center">
        <i className="fas fa-spinner fa-spin text-4xl text-green-600"></i>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50 p-4 sm:p-6" data-testid="materiales-clases-page">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent flex items-center gap-2">
                <i className="fas fa-folder-open" />
                Materiales de Clases
              </h1>
              <p className="text-gray-600 mt-1">
                {user.rol === 'estudiante' || user.rol === 'pastor' || user.rol === 'graduado'
                  ? 'Materiales de tus clases inscritas'
                  : user.rol === 'profesor'
                  ? 'Materiales de tus clases'
                  : 'Vista administrativa de todos los materiales'
                }
              </p>
            </div>
          </div>

          {/* Filtros */}
          <div className="flex flex-wrap items-center gap-2 mt-4">
            <span className="text-sm text-gray-600 flex items-center">
              <i className="fas fa-filter mr-2"></i>
              Filtrar:
            </span>
            <Button size="sm" variant={filtroEstado === 'todas' ? 'default' : 'outline'}
              onClick={() => setFiltroEstado('todas')}
              className={filtroEstado === 'todas' ? 'bg-green-600 hover:bg-green-700' : ''}>
              Todas
            </Button>
            <Button size="sm" variant={filtroEstado === 'activas' ? 'default' : 'outline'}
              onClick={() => setFiltroEstado('activas')}
              className={filtroEstado === 'activas' ? 'bg-green-600 hover:bg-green-700' : ''}>
              <i className="fas fa-check-circle mr-1"></i>Activas
            </Button>
            <Button size="sm" variant={filtroEstado === 'inactivas' ? 'default' : 'outline'}
              onClick={() => setFiltroEstado('inactivas')}
              className={filtroEstado === 'inactivas' ? 'bg-gray-600 hover:bg-gray-700' : ''}>
              <i className="fas fa-times-circle mr-1"></i>Inactivas
            </Button>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mt-6">
            <div className="bg-gradient-to-r from-green-50 to-green-100 rounded-xl p-4">
              <div className="text-2xl font-bold text-green-600">{totalMateriales}</div>
              <div className="text-xs text-green-700">Total Materiales</div>
            </div>
            <div className="bg-gradient-to-r from-emerald-50 to-emerald-100 rounded-xl p-4">
              <div className="text-2xl font-bold text-emerald-600">{clasesConMateriales}</div>
              <div className="text-xs text-emerald-700">Clases con Material</div>
            </div>
            <div className="bg-gradient-to-r from-teal-50 to-teal-100 rounded-xl p-4">
              <div className="text-2xl font-bold text-teal-600">{clasesFiltradas.length}</div>
              <div className="text-xs text-teal-700">Clases</div>
            </div>
          </div>
        </div>

        {/* Cards por clase */}
        {clasesFiltradas.length === 0 ? (
          <Card className="shadow-lg">
            <CardContent className="text-center py-12">
              <i className="fas fa-folder-open text-gray-300 text-6xl mb-4"></i>
              <h3 className="text-xl font-semibold text-gray-700 mb-2">No hay clases disponibles</h3>
              <p className="text-gray-500">No tienes clases {filtroEstado !== 'todas' ? filtroEstado : ''} con acceso a materiales.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {clasesFiltradas.map(clase => {
              const materiales = materialesPorClase[clase.id] || [];
              return (
                <Card key={clase.id} className="overflow-hidden hover:shadow-xl transition-all duration-300 border-2 border-transparent hover:border-green-300" data-testid={`material-clase-card-${clase.id}`}>
                  {/* Banner */}
                  <div className="h-28 sm:h-32 w-full relative" style={getBannerStyle(clase.banner_url)}>
                    <div className="absolute inset-0 flex items-center justify-between p-4">
                      <div>
                        <h3 className="text-white text-xl sm:text-2xl font-bold drop-shadow-lg">{clase.titulo}</h3>
                        <p className="text-white/90 text-sm drop-shadow">
                          <i className="fas fa-user mr-1" />{clase.profesor_nombre}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge className={`${clase.activa ? 'bg-green-500' : 'bg-gray-500'} text-white`}>
                          {clase.activa ? 'Activa' : 'Inactiva'}
                        </Badge>
                        <Badge className="bg-white/20 text-white backdrop-blur-sm">
                          <i className="fas fa-file mr-1" />{materiales.length} archivos
                        </Badge>
                      </div>
                    </div>
                  </div>

                  <CardContent className="p-4 sm:p-6">
                    {materiales.length === 0 ? (
                      <div className="text-center py-6 text-gray-400">
                        <i className="fas fa-folder-open text-3xl mb-2"></i>
                        <p className="text-sm">No hay materiales disponibles</p>
                        {(isAdmin || (isProfesor && clase.profesor_id === user.id)) && (
                          <Button size="sm" variant="outline" className="mt-3"
                            onClick={() => navigate(`/app/clases/${clase.id}/materiales`)}>
                            <i className="fas fa-plus mr-2"></i>Agregar Material
                          </Button>
                        )}
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {materiales.map((mat, idx) => (
                          <div key={mat.id || idx} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors group">
                            <i className={`${getFileIcon(mat.archivo_nombre || mat.nombre_archivo)} text-xl flex-shrink-0`}></i>
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-gray-800 text-sm truncate">
                                {mat.descripcion || mat.archivo_nombre || mat.nombre_archivo || 'Material'}
                              </p>
                              {mat.tipo === 'google_drive_link' && (
                                <span className="text-xs text-blue-500"><i className="fab fa-google-drive mr-1"></i>Google Drive</span>
                              )}
                              {mat.fecha_subida && (
                                <span className="text-xs text-gray-400 ml-2">
                                  {new Date(mat.fecha_subida).toLocaleDateString('es-UY')}
                                </span>
                              )}
                            </div>
                            <Button size="sm" variant="ghost" className="opacity-70 group-hover:opacity-100"
                              onClick={() => {
                                if (mat.tipo === 'google_drive_link' && mat.google_drive_link) {
                                  window.open(mat.google_drive_link, '_blank');
                                } else if (mat.archivo_url || mat.url) {
                                  const raw = mat.archivo_url || mat.url;
                                  if (raw.startsWith('http')) {
                                    window.open(raw, '_blank');
                                  } else {
                                    // Normalizar /uploads/... o /api/uploads/... → /api/files/...
                                    const filename = raw.replace(/^\/api\/files\//, '').replace(/^\/uploads\//, '').replace(/^\/api\/uploads\//, '');
                                    const backendBase = API.replace('/api', '');
                                    window.open(`${backendBase}/api/files/${filename}`, '_blank');
                                  }
                                }
                              }}>
                              <i className="fas fa-download text-green-600"></i>
                            </Button>
                          </div>
                        ))}

                        {/* Button to manage */}
                        {(isAdmin || (isProfesor && clase.profesor_id === user.id)) && (
                          <div className="pt-3 border-t">
                            <Button size="sm" variant="outline" className="w-full"
                              onClick={() => navigate(`/app/clases/${clase.id}/materiales`)}
                              data-testid={`gestionar-materiales-${clase.id}`}>
                              <i className="fas fa-cog mr-2"></i>Gestionar Materiales
                            </Button>
                          </div>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default MaterialesClases;
